import * as THREE from "three";
import {
  Product3DAssetBounds,
  Product3DDimensions,
} from "../../products/product3DTypes";

export type Symbol3DAssetTransformInput = {
  modelBounds: THREE.Box3;
  targetDimensions: Product3DDimensions;
  baseElevation?: number;
  fitMode?: "contain3d" | "footprint" | "stretchFootprint";
};

export type Symbol3DAssetTransform = {
  scale: number | [number, number, number];
  position: [number, number, number];
};

export type Symbol3DAssetFitMode =
  | "contain3d"
  | "footprint"
  | "stretchFootprint";

/**
 * Chooses how a GLB fills its declared width/depth/height.
 *
 * Products and uploaded models always stretch to the declared dimensions so the
 * 3D mesh matches the 2D footprint. Aspect-ratio lock only couples
 * property-panel edits; using contain/footprint here undersized models whenever
 * mesh bounds differed from catalog (or stored) dimensions.
 *
 * Standard library assets keep contain3d so authored proportions stay intact.
 *
 * @param _lockAspectRatio Kept for call-site compatibility; does not affect fit.
 */
export function getSymbol3DAssetFitMode(
  source: "userModel" | "product" | "standard",
  _lockAspectRatio: boolean,
): Symbol3DAssetFitMode {
  if (source === "userModel" || source === "product") {
    return "stretchFootprint";
  }
  return "contain3d";
}

const MIN_MODEL_SIZE = 0.01;

export function getSymbol3DAssetScaleComponents(
  scale: number | [number, number, number],
): [number, number, number] {
  if (Array.isArray(scale)) return scale;
  return [scale, scale, scale];
}

export function computeFloorplannerAssetTransform(
  input: Symbol3DAssetTransformInput,
): Symbol3DAssetTransform {
  const size = input.modelBounds.getSize(new THREE.Vector3());
  const center = input.modelBounds.getCenter(new THREE.Vector3());
  const width = positiveOrFallback(input.targetDimensions.width, size.x);
  const depth = positiveOrFallback(input.targetDimensions.depth, size.y);
  const height = positiveOrFallback(input.targetDimensions.height, size.z);
  const footprintScale = Math.min(
    width / Math.max(size.x, MIN_MODEL_SIZE),
    depth / Math.max(size.y, MIN_MODEL_SIZE),
  );
  const footprintScaleX = width / Math.max(size.x, MIN_MODEL_SIZE);
  const footprintScaleY = depth / Math.max(size.y, MIN_MODEL_SIZE);
  const heightScale = height / Math.max(size.z, MIN_MODEL_SIZE);
  const uniformScale = Math.min(footprintScale, heightScale);
  const scale: number | [number, number, number] =
    input.fitMode === "stretchFootprint"
      ? [
          footprintScaleX,
          footprintScaleY,
          Number.isFinite(heightScale) && heightScale > 0 ? heightScale : 1,
        ]
      : input.fitMode === "footprint"
      ? [
          footprintScale,
          footprintScale,
          Number.isFinite(heightScale) && heightScale > 0 ? heightScale : 1,
        ]
      : uniformScale;
  const [scaleX, scaleY, scaleZ] = getSymbol3DAssetScaleComponents(scale);
  const baseElevation = input.baseElevation ?? 0;

  return {
    scale,
    position: [
      normalizeZero(-center.x * scaleX),
      normalizeZero(-center.y * scaleY),
      normalizeZero(baseElevation - input.modelBounds.min.z * scaleZ),
    ],
  };
}

export function dimensionsFromAssetBounds(
  bounds: Product3DAssetBounds | undefined,
): Product3DDimensions | undefined {
  if (!bounds) return undefined;
  if (bounds.size) return bounds.size;
  if (!bounds.min || !bounds.max) return undefined;
  return {
    width: positiveDimension(bounds.max[0] - bounds.min[0]),
    depth: positiveDimension(bounds.max[1] - bounds.min[1]),
    height: positiveDimension(bounds.max[2] - bounds.min[2]),
  };
}

function positiveOrFallback(
  value: number | undefined,
  fallback: number,
): number {
  return positiveDimension(value) ?? Math.max(fallback, MIN_MODEL_SIZE);
}

function positiveDimension(value: number | undefined): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : undefined;
}

function normalizeZero(value: number): number {
  return Object.is(value, -0) ? 0 : value;
}
